import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPurchaseOrders = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);

  useEffect(() => {
    fetchPurchaseOrders();
    fetchRoutes();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      const res = await axios.get('/api/admin/purchase-orders');
      setPurchaseOrders(res.data);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const res = await axios.get('/api/admin/routes');
      // Filter routes that are completed and don't have a PO yet
      const availableRoutes = res.data.filter(route => 
        route.status === 'completed' && !route.purchaseOrderId
      );
      setRoutes(availableRoutes);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    if (selectedRoutes.length === 0) {
      alert('Please select at least one route');
      return;
    }

    try {
      await axios.post('/api/admin/purchase-orders/create', {
        routeIds: selectedRoutes
      });
      setShowCreateForm(false);
      setSelectedRoutes([]);
      fetchPurchaseOrders();
      fetchRoutes();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create purchase order');
    }
  };

  const handleSendPO = async (id) => {
    try {
      await axios.put(`/api/admin/purchase-orders/${id}/send`);
      fetchPurchaseOrders();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send purchase order');
    }
  };

  const toggleRouteSelection = (routeId) => {
    setSelectedRoutes(prev => 
      prev.includes(routeId)
        ? prev.filter(id => id !== routeId)
        : [...prev, routeId]
    );
  };

  const getStatusBadge = (status) => {
    const statusClass = `badge badge-${status.toLowerCase().replace(' ', '-')}`;
    return <span className={statusClass}>{status}</span>;
  };

  if (loading) {
    return <div className="loading">Loading purchase orders...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Purchase Orders</h2>
        {routes.length > 0 && (
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            Create Purchase Order
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Create Purchase Order</h3>
          <p>Select routes to associate (must belong to the same transportation company):</p>
          <form onSubmit={handleCreatePO}>
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
              {routes.length === 0 ? (
                <p>No available routes to create purchase order</p>
              ) : (
                routes.map(route => (
                  <div key={route._id} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedRoutes.includes(route._id)}
                        onChange={() => toggleRouteSelection(route._id)}
                        style={{ marginRight: '10px' }}
                      />
                      <div>
                        <strong>{route.routeNumber}</strong> - {route.startPoint} to {route.endPoint}
                        <br />
                        <small>Transporte: {route.assignedTo?.companyName || route.assignedTo?.name || 'N/A'}</small>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
            <button type="submit" className="btn btn-success" disabled={selectedRoutes.length === 0}>
              Create Purchase Order
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => {
              setShowCreateForm(false);
              setSelectedRoutes([]);
            }} style={{ marginLeft: '10px' }}>
              Cancel
            </button>
          </form>
        </div>
      )}

      {purchaseOrders.length === 0 ? (
        <div className="card">
          <p>No purchase orders found.</p>
        </div>
      ) : (
        <div>
          {purchaseOrders.map(po => (
            <div key={po._id} className="card">
              <h3>PO Number: {po.poNumber}</h3>
              <p><strong>Status:</strong> {getStatusBadge(po.status)}</p>
              <p><strong>Total Amount:</strong> ${po.totalAmount}</p>
              <p><strong>Transportation Company:</strong> {po.transportationCompany?.name || 'N/A'}</p>
              <p><strong>Issued At:</strong> {new Date(po.issuedAt).toLocaleString()}</p>
              <div style={{ marginTop: '15px' }}>
                <h4>Associated Routes:</h4>
                <ul>
                  {po.routes.map(route => (
                    <li key={route._id}>
                      {route.routeNumber} - {route.startPoint} to {route.endPoint} (${route.price})
                    </li>
                  ))}
                </ul>
              </div>
              {po.pdfPath && (
                <div style={{ marginTop: '15px' }}>
                  <a 
                    href={`http://localhost:5000${po.pdfPath}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ textDecoration: 'none', display: 'inline-block' }}
                  >
                    Download PDF
                  </a>
                </div>
              )}
              {po.status === 'pending' && (
                <button className="btn btn-primary" onClick={() => handleSendPO(po._id)} style={{ marginTop: '15px', marginLeft: po.pdfPath ? '10px' : '0' }}>
                  Send to Transportation Company
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPurchaseOrders;
